import { createContext, useContext, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import css from "./studio.module.css";

/**
 * The single action zone — W-2, spec FR-8 and FR-9.
 *
 * A screen renders `<StudioActions>` wherever is convenient in its own tree;
 * the buttons come out in the shell's sticky bar at the foot of the viewport.
 * Screens never draw a persistence control themselves.
 *
 * **This is the mechanism, not a tidy-up.** The bug it removes is a control
 * appearing on a screen it cannot act on: the shell's `SAVE CHANGES` was
 * rendered unconditionally and, on the asset form, saved the wrong record. With
 * no way for a screen to draw its own save, and one bar that only exists when a
 * screen fills it, that class of defect cannot recur.
 *
 * A portal rather than context state, deliberately. Passing `ReactNode` up
 * through `setState` re-registers on every render — JSX has a fresh identity
 * each time — which is a render loop unless every screen remembers to memoise.
 * A portal has no such trap: React reconciles it like any other child.
 */

const SlotContext = createContext<HTMLElement | null>(null);

/**
 * Renders the sticky bar and provides it as a portal target.
 *
 * The bar is `:empty`-hidden in CSS, so a screen that declares nothing — the
 * asset *list* — gets no bar at all rather than an empty strip. That check
 * lives in CSS because it needs no state and cannot go stale.
 */
export function StudioActionZone({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  return (
    <SlotContext.Provider value={slot}>
      {children}
      <div className={css.actionBar} ref={setSlot} />
    </SlotContext.Provider>
  );
}

/**
 * Declares a screen's actions and its status line.
 *
 * `status` is the mono line reporting dirty state, refusal or confirmation
 * (FR-10, FR-11). It sits beside the buttons because a refusal that is not
 * next to the button it refuses has to be hunted for.
 */
export function StudioActions({
  status,
  children,
}: {
  status?: ReactNode;
  children: ReactNode;
}) {
  const slot = useContext(SlotContext);
  // Null on the first render, before the bar's ref callback has run. One
  // frame late is correct; rendering the buttons inline would put them
  // momentarily inside the content column, which is the thing being removed.
  if (!slot) return null;

  return createPortal(
    <>
      <div className={css.actionStatus}>{status}</div>
      <div className={css.actionButtons}>{children}</div>
    </>,
    slot
  );
}
